/** Session 8 Block 2 · Operator plans catalog page. */
import React, { useEffect, useState } from "react";
import { api } from "../../lib/api";
import { toast } from "sonner";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import {
  Card, CardContent, CardHeader, CardTitle,
} from "../../components/ui/card";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle,
          DialogFooter, DialogTrigger }
  from "../../components/ui/dialog";
import { Loader2, Pencil, Plus, Trash2 } from "lucide-react";


function PriceCents({ cents, custom }) {
  if (custom) return <span>Custom</span>;
  return <span>{(cents / 100).toFixed(2)} EUR</span>;
}


function PlanEditor({ plan, featureKeys, limitKeys, onSaved, onClose }) {
  const [name, setName] = useState(plan?.name || "");
  const [priceCents, setPriceCents] = useState(plan?.price_monthly_cents || 0);
  const [features, setFeatures] = useState(plan?.features || {});
  const [limits, setLimits] = useState(plan?.limits || {});
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      const body = { name, price_monthly_cents: Number(priceCents),
                       features, limits };
      const r = await api.put(`/operator/plans/${plan.plan_id}`, body);
      onSaved && onSaved(r.data);
      toast.success("Plan aktualisiert.");
    } catch (e) {
      toast.error(e.response?.data?.detail?.error || "save_failed");
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={true} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl"
                       data-testid="operator-plan-editor">
        <DialogHeader>
          <DialogTitle>Plan bearbeiten · {plan.slug}</DialogTitle>
        </DialogHeader>
        <div className="grid gap-4 md:grid-cols-2">
          <div>
            <Label>Name</Label>
            <Input value={name} onChange={(e) => setName(e.target.value)}
                    data-testid={`plan-edit-name-${plan.slug}`} />
          </div>
          <div>
            <Label>Preis / Monat (Cent)</Label>
            <Input type="number" value={priceCents}
                    onChange={(e) => setPriceCents(e.target.value)}
                    data-testid={`plan-edit-price-${plan.slug}`} />
          </div>
        </div>
        <div>
          <Label className="mb-1 block">Features</Label>
          <div className="grid gap-1 md:grid-cols-2">
            {featureKeys.map((k) => (
              <label key={k} className="text-xs flex items-center gap-2">
                <input type="checkbox"
                        checked={!!features[k]}
                        onChange={(e) => setFeatures({
                          ...features, [k]: e.target.checked })}
                        data-testid={`plan-feature-${plan.slug}-${k}`} />
                {k}
              </label>
            ))}
          </div>
        </div>
        <div>
          <Label className="mb-1 block">Limits (-1 = unlimited)</Label>
          <div className="grid gap-2 md:grid-cols-2">
            {limitKeys.map((k) => (
              <div key={k} className="flex items-center gap-2 text-xs">
                <span className="min-w-[190px]">{k}</span>
                <Input type="number" value={limits[k] ?? 0}
                        onChange={(e) => setLimits({
                          ...limits, [k]: Number(e.target.value) })}
                        data-testid={`plan-limit-${plan.slug}-${k}`} />
              </div>
            ))}
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>Abbrechen</Button>
          <Button onClick={save} disabled={saving}
                    data-testid={`plan-save-${plan.slug}`}>
            {saving ? "Speichern…" : "Speichern"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


export default function OperatorPlans() {
  const [plans, setPlans] = useState([]);
  const [featureKeys, setFeatureKeys] = useState([]);
  const [limitKeys, setLimitKeys] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);

  const load = () => {
    setLoading(true);
    api.get("/operator/plans")
      .then((r) => {
        setPlans(r.data.plans || []);
        setFeatureKeys(r.data.feature_keys || []);
        setLimitKeys(r.data.limit_keys || []);
      })
      .catch(() => toast.error("Konnte Plans nicht laden."))
      .finally(() => setLoading(false));
  };
  useEffect(load, []);

  return (
    <div className="p-6 max-w-5xl mx-auto space-y-4"
          data-testid="operator-plans-page">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-semibold">Abos & Pläne</h1>
      </div>
      {loading && <Loader2 className="h-4 w-4 animate-spin" />}
      <div className="grid gap-4 md:grid-cols-2">
        {plans.map((p) => (
          <Card key={p.plan_id}
                 data-testid={`operator-plan-card-${p.slug}`}>
            <CardHeader className="pb-2">
              <CardTitle className="flex items-center justify-between">
                <span>{p.name}</span>
                <Badge variant="secondary">{p.slug}</Badge>
              </CardTitle>
              <div className="text-sm text-muted-foreground">
                <PriceCents cents={p.price_monthly_cents}
                              custom={p.custom_pricing} />
                {" "}/ Monat
              </div>
            </CardHeader>
            <CardContent>
              <div className="text-xs text-muted-foreground mb-2">
                Features: {Object.entries(p.features || {})
                             .filter(([, v]) => v).length}
                {" · "}Limits: {Object.keys(p.limits || {}).length}
              </div>
              <div className="flex gap-2">
                <Button size="sm" variant="secondary"
                        onClick={() => setEditing(p)}
                        data-testid={`btn-edit-plan-${p.slug}`}>
                  <Pencil className="h-3 w-3 mr-1" />
                  Bearbeiten
                </Button>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
      {editing && (
        <PlanEditor plan={editing}
                       featureKeys={featureKeys}
                       limitKeys={limitKeys}
                       onSaved={() => { setEditing(null); load(); }}
                       onClose={() => setEditing(null)} />
      )}
    </div>
  );
}
